import { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from '@/components/ui/dialog'
import {
  X,
  ChevronUp,
  ChevronDown,
  Replace,
  CheckCheck,
  Search,
  Lock,
  AlertCircle
} from 'lucide-react'

/** 匹配范围：仅原文 / 仅译文 / 同时搜索原文和译文
 *  注意：替换只能改译文，原文只读，即使匹配到原文也不能替换原文内容
 */
type SearchScope = 'source' | 'target' | 'both'

/** 单条匹配结果：定位到某段某位置，用于跳转和高亮 */
interface Match {
  segmentId: string
  segmentIndex: number
  field: 'source' | 'target'
  start: number
  end: number
  matchedText: string
  /** 该段落是否被锁定（锁定段落替换时跳过，但查找仍可命中） */
  isLocked: boolean
  /** 该段落是否已确认（替换后会重置为未确认） */
  isConfirmed: boolean
}

interface SearchReplacePanelProps {
  /** 项目所有段落（用于内存搜索） */
  segments: Array<{
    id: string
    sourceText: string
    translation?: string
    isLocked: boolean
    isConfirmed: boolean
  }>
  /** 跳转到指定段落索引（复用现有 focusIndex 机制） */
  onNavigate: (segmentIndex: number) => void
  /** 关闭浮层 */
  onClose: () => void
  /** 替换提交：传入批量更新 payload，由父组件调用 batchUpdate */
  onSubmitReplace: (
    updates: Array<{ id: string; translation: string; isConfirmed: boolean }>
  ) => void
}

/** 在文本中查找所有匹配位置（支持正则/大小写敏感/整词匹配）
 *  返回匹配数组，每个元素包含 start/end 索引和匹配文本
 */
function findMatchesInText(
  text: string,
  pattern: string,
  options: { caseSensitive: boolean; wholeWord: boolean; useRegex: boolean }
): Array<{ start: number; end: number; matchedText: string }> {
  if (!text || !pattern) return []

  try {
    let regex: RegExp

    if (options.useRegex) {
      // 正则模式：用户输入即为正则源；非法正则会被外层 try-catch 捕获返回空
      regex = new RegExp(pattern, options.caseSensitive ? 'g' : 'gi')
    } else {
      // 普通文本模式：转义正则元字符
      const escaped = pattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      if (options.wholeWord) {
        // 整词匹配：前后加 \b 边界
        regex = new RegExp(`\\b${escaped}\\b`, options.caseSensitive ? 'g' : 'gi')
      } else {
        regex = new RegExp(escaped, options.caseSensitive ? 'g' : 'gi')
      }
    }

    const results: Array<{ start: number; end: number; matchedText: string }> = []
    let match: RegExpExecArray | null
    // 用 while + exec 全局匹配，避免 String.match 在大文本上的内存问题
    while ((match = regex.exec(text)) !== null) {
      // 防止零宽匹配（如正则 `` 或 `(?=)`）导致死循环
      if (match[0].length === 0) {
        regex.lastIndex++
        continue
      }
      results.push({
        start: match.index,
        end: match.index + match[0].length,
        matchedText: match[0],
      })
    }
    return results
  } catch (e) {
    // 非法正则：返回空数组，UI 由调用方显示错误提示
    return []
  }
}

export function SearchReplacePanel({
  segments,
  onNavigate,
  onClose,
  onSubmitReplace,
}: SearchReplacePanelProps) {
  const [searchText, setSearchText] = useState('')
  const [replaceText, setReplaceText] = useState('')
  const [scope, setScope] = useState<SearchScope>('target')
  const [caseSensitive, setCaseSensitive] = useState(false)
  const [wholeWord, setWholeWord] = useState(false)
  const [useRegex, setUseRegex] = useState(false)
  const [currentMatchIdx, setCurrentMatchIdx] = useState(0)
  const [showConfirmAll, setShowConfirmAll] = useState(false)
  const [regexError, setRegexError] = useState<string | null>(null)

  const searchInputRef = useRef<HTMLInputElement>(null)
  const replaceInputRef = useRef<HTMLInputElement>(null)

  // 浮层打开时自动聚焦查找输入框
  useEffect(() => {
    searchInputRef.current?.focus()
  }, [])

  // 计算所有匹配结果（依赖 searchText/scope/options/segments）
  // 用 useMemo 缓存，避免每次渲染都重算（大文档 500+ 段时性能关键）
  const matches = useMemo<Match[]>(() => {
    if (!searchText.trim()) return []

    const options = { caseSensitive, wholeWord, useRegex }
    const results: Match[] = []

    segments.forEach((seg, segIndex) => {
      // 按范围决定搜索哪些字段
      const fieldsToSearch: Array<'source' | 'target'> = []
      if (scope === 'source' || scope === 'both') fieldsToSearch.push('source')
      if (scope === 'target' || scope === 'both') fieldsToSearch.push('target')

      for (const field of fieldsToSearch) {
        const text = field === 'source' ? seg.sourceText : (seg.translation || '')
        if (!text) continue

        const fieldMatches = findMatchesInText(text, searchText, options)
        for (const m of fieldMatches) {
          results.push({
            segmentId: seg.id,
            segmentIndex: segIndex,
            field,
            start: m.start,
            end: m.end,
            matchedText: m.matchedText,
            isLocked: seg.isLocked,
            isConfirmed: seg.isConfirmed,
          })
        }
      }
    })

    return results
  }, [searchText, scope, caseSensitive, wholeWord, useRegex, segments])

  // 统计：总匹配数、匹配段落数、锁定段落数（替换时会被跳过）
  const stats = useMemo(() => {
    const totalMatches = matches.length
    const matchedSegments = new Set(matches.map(m => m.segmentId)).size
    const lockedMatches = matches.filter(m => m.isLocked).length
    return { totalMatches, matchedSegments, lockedMatches }
  }, [matches])

  // 译文匹配（用于替换）：只统计 field=target 且未锁定的匹配
  const replaceableMatches = useMemo(() => {
    return matches.filter(m => m.field === 'target' && !m.isLocked)
  }, [matches])

  // 正则合法性检查（useRegex 开启时）
  useEffect(() => {
    if (useRegex && searchText.trim()) {
      try {
        new RegExp(searchText, caseSensitive ? 'g' : 'gi')
        setRegexError(null)
      } catch (e: any) {
        setRegexError(e.message || '正则表达式非法')
      }
    } else {
      setRegexError(null)
    }
  }, [useRegex, searchText, caseSensitive])

  // 匹配数变化时重置当前索引到 0
  useEffect(() => {
    setCurrentMatchIdx(0)
  }, [searchText, scope, caseSensitive, wholeWord, useRegex])

  // 跳转到当前匹配段落
  const navigateToCurrent = useCallback(() => {
    if (matches.length === 0) return
    const current = matches[currentMatchIdx]
    if (current) {
      onNavigate(current.segmentIndex)
    }
  }, [matches, currentMatchIdx, onNavigate])

  // 当前匹配变化时自动跳转
  useEffect(() => {
    navigateToCurrent()
  }, [navigateToCurrent])

  // 上一处/下一处
  const goPrev = () => {
    if (matches.length === 0) return
    setCurrentMatchIdx(prev => (prev - 1 + matches.length) % matches.length)
  }
  const goNext = () => {
    if (matches.length === 0) return
    setCurrentMatchIdx(prev => (prev + 1) % matches.length)
  }

  // 替换当前匹配：只替换当前高亮的那一处
  // 注意：替换只作用于译文，即使当前匹配在原文也只跳转不替换
  const handleReplaceCurrent = () => {
    if (matches.length === 0) return
    const current = matches[currentMatchIdx]
    if (!current) return

    // 当前匹配在原文：不能替换原文，提示并跳过
    if (current.field === 'source') {
      return
    }

    // 当前匹配在锁定段落：跳过
    if (current.isLocked) {
      return
    }

    const seg = segments[current.segmentIndex]
    const originalTranslation = seg.translation || ''
    // 执行替换：用 slice 拼接，保留匹配前后的内容
    const newTranslation =
      originalTranslation.slice(0, current.start) +
      replaceText +
      originalTranslation.slice(current.end)

    // 提交单条替换：isConfirmed 重置为 false（译文变了需重新确认）
    onSubmitReplace([
      {
        id: seg.id,
        translation: newTranslation,
        isConfirmed: false,
      },
    ])

    // 替换后当前索引可能失效（matches 会重新计算），停留在原位置等待重算
  }

  // 全部替换：替换所有译文匹配（排除锁定段落）
  // 按段落聚合，每个段落可能有多处匹配，需从后往前替换避免索引偏移
  const handleReplaceAll = () => {
    setShowConfirmAll(true)
  }

  // 确认全部替换（AlertDialog 确认后执行）
  const confirmReplaceAll = () => {
    // 按段落聚合匹配
    const segMatchesMap = new Map<string, Match[]>()
    for (const m of replaceableMatches) {
      if (!segMatchesMap.has(m.segmentId)) {
        segMatchesMap.set(m.segmentId, [])
      }
      segMatchesMap.get(m.segmentId)!.push(m)
    }

    const updates: Array<{ id: string; translation: string; isConfirmed: boolean }> = []
    for (const [segId, segMatches] of segMatchesMap) {
      const seg = segments.find(s => s.id === segId)
      if (!seg) continue
      let translation = seg.translation || ''
      // 从后往前替换，避免前面的替换影响后面的索引
      const sortedMatches = [...segMatches].sort((a, b) => b.start - a.start)
      for (const m of sortedMatches) {
        translation = translation.slice(0, m.start) + replaceText + translation.slice(m.end)
      }
      updates.push({
        id: segId,
        translation,
        isConfirmed: false, // 译文变了需重新确认
      })
    }

    if (updates.length > 0) {
      onSubmitReplace(updates)
    }
    setShowConfirmAll(false)
  }

  // 键盘快捷键：Enter 下一处，Shift+Enter 上一处，Cmd+Enter 替换当前
  const handleKeyDown = (e: React.KeyboardEvent, context: 'search' | 'replace') => {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (e.shiftKey) {
        goPrev()
      } else if ((e.metaKey || e.ctrlKey) && context === 'replace') {
        handleReplaceCurrent()
      } else {
        goNext()
      }
    } else if (e.key === 'Escape') {
      e.preventDefault()
      onClose()
    }
  }

  const hasMatches = matches.length > 0
  const currentMatch = matches[currentMatchIdx]
  // 当前匹配是否可替换（译文 + 未锁定）
  const canReplaceCurrent =
    currentMatch &&
    currentMatch.field === 'target' &&
    !currentMatch.isLocked

  return (
    <>
      <div className="fixed top-4 right-4 z-50 w-96 bg-background border rounded-lg shadow-2xl">
        {/* 头部：标题 + 关闭按钮 */}
        <div className="flex items-center justify-between px-3 py-2 border-b">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Search className="h-4 w-4" />
            查找替换
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
            onClick={onClose}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>

        <div className="p-3 space-y-2">
          {/* 查找输入框 */}
          <div className="relative">
            <Input
              ref={searchInputRef}
              type="text"
              placeholder="查找内容..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={(e) => handleKeyDown(e, 'search')}
              className="pr-20"
            />
            <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-0.5">
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0"
                onClick={goPrev}
                disabled={!hasMatches}
                title="上一处 (Shift+Enter)"
              >
                <ChevronUp className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0"
                onClick={goNext}
                disabled={!hasMatches}
                title="下一处 (Enter)"
              >
                <ChevronDown className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>

          {/* 替换输入框 */}
          <div className="relative">
            <Input
              ref={replaceInputRef}
              type="text"
              placeholder="替换为...（仅作用于译文）"
              value={replaceText}
              onChange={(e) => setReplaceText(e.target.value)}
              onKeyDown={(e) => handleKeyDown(e, 'replace')}
              className="pr-20"
            />
            <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-0.5">
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-[10px]"
                onClick={handleReplaceCurrent}
                disabled={!canReplaceCurrent}
                title="替换当前 (Cmd+Enter)"
              >
                <Replace className="h-3 w-3 mr-1" />
                替换
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-[10px]"
                onClick={handleReplaceAll}
                disabled={replaceableMatches.length === 0}
                title="全部替换"
              >
                <CheckCheck className="h-3 w-3 mr-1" />
                全部
              </Button>
            </div>
          </div>

          {/* 范围 radio + 选项 checkbox */}
          <div className="flex items-center justify-between gap-2 text-[11px]">
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-1 cursor-pointer">
                <input
                  type="radio"
                  name="scope"
                  checked={scope === 'source'}
                  onChange={() => setScope('source')}
                  className="h-3 w-3"
                />
                原文
              </label>
              <label className="flex items-center gap-1 cursor-pointer">
                <input
                  type="radio"
                  name="scope"
                  checked={scope === 'target'}
                  onChange={() => setScope('target')}
                  className="h-3 w-3"
                />
                译文
              </label>
              <label className="flex items-center gap-1 cursor-pointer">
                <input
                  type="radio"
                  name="scope"
                  checked={scope === 'both'}
                  onChange={() => setScope('both')}
                  className="h-3 w-3"
                />
                全部
              </label>
            </div>
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-1 cursor-pointer">
                <Checkbox
                  checked={caseSensitive}
                  onCheckedChange={(v) => setCaseSensitive(!!v)}
                  className="h-3 w-3"
                />
                区分大小写
              </label>
              <label className="flex items-center gap-1 cursor-pointer">
                <Checkbox
                  checked={wholeWord}
                  onCheckedChange={(v) => setWholeWord(!!v)}
                  className="h-3 w-3"
                />
                整词
              </label>
              <label className="flex items-center gap-1 cursor-pointer">
                <Checkbox
                  checked={useRegex}
                  onCheckedChange={(v) => setUseRegex(!!v)}
                  className="h-3 w-3"
                />
                正则
              </label>
            </div>
          </div>

          {/* 正则错误提示 */}
          {regexError && (
            <div className="flex items-start gap-1 text-[10px] text-destructive bg-destructive/5 px-2 py-1 rounded">
              <AlertCircle className="h-3 w-3 mt-0.5 flex-shrink-0" />
              <span>{regexError}</span>
            </div>
          )}

          {/* 统计信息 */}
          <div className="flex items-center justify-between text-[10px] text-muted-foreground border-t pt-2">
            <span>
              {hasMatches ? (
                <>
                  第 <span className="text-foreground font-medium">{currentMatchIdx + 1}</span> /{' '}
                  {stats.totalMatches} 处 · {stats.matchedSegments} 个段落
                </>
              ) : searchText.trim() ? (
                '无匹配'
              ) : (
                '输入查找内容'
              )}
            </span>
            {stats.lockedMatches > 0 && (
              <span className="flex items-center gap-1 text-warning">
                <Lock className="h-2.5 w-2.5" />
                {stats.lockedMatches} 处锁定跳过
              </span>
            )}
          </div>

          {/* 当前匹配定位提示 */}
          {currentMatch && (
            <div className="text-[10px] text-muted-foreground">
              当前：#{currentMatch.segmentIndex + 1} 段
              {currentMatch.field === 'source' ? '（原文，不可替换）' : '（译文）'}
              {currentMatch.isLocked && ' · 锁定'}
            </div>
          )}
        </div>
      </div>

      {/* 全部替换确认对话框 */}
      <Dialog open={showConfirmAll} onOpenChange={setShowConfirmAll}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>确认全部替换？</DialogTitle>
            <DialogDescription>
              将替换 <span className="font-medium text-foreground">{replaceableMatches.length}</span> 处译文匹配
              （涉及 {new Set(replaceableMatches.map(m => m.segmentId)).size} 个段落）
              {stats.lockedMatches > 0 && `，${stats.lockedMatches} 处锁定段落将被跳过`}。
              <br />
              <br />
              替换后这些段落将变为未确认状态，需重新确认。
              <br />
              <span className="text-muted-foreground">替换内容："{searchText}" → "{replaceText}"</span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">取消</Button>
            </DialogClose>
            <Button onClick={confirmReplaceAll}>
              确认替换
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
